import { useMemo, useState, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import {
  Home,
  Newspaper,
  Users,
  Wine,
  GraduationCap,
  Menu,
  LogOut,
  X,
} from 'lucide-react'
import logoUrl from '@/assets/logo.png'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSession } from '@/features/auth/session'
import type { UserRole } from '@/features/auth/types'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  /** Roles que pueden ver esta entrada. */
  roles: readonly UserRole[]
}

const ADMIN_ONLY: readonly UserRole[] = ['ADMIN']
const STAFF: readonly UserRole[] = ['ADMIN', 'DOCENTE']

const navItems: readonly NavItem[] = [
  { to: '/dashboard', label: 'Inicio', icon: <Home className="h-4 w-4" />, roles: ADMIN_ONLY },
  { to: '/dashboard/fichas', label: 'Fichas de cata', icon: <Wine className="h-4 w-4" />, roles: STAFF },
  { to: '/dashboard/docentes', label: 'Docentes', icon: <GraduationCap className="h-4 w-4" />, roles: ADMIN_ONLY },
  { to: '/dashboard/noticias', label: 'Noticias', icon: <Newspaper className="h-4 w-4" />, roles: ADMIN_ONLY },
  { to: '/dashboard/usuarios', label: 'Usuarios', icon: <Users className="h-4 w-4" />, roles: ADMIN_ONLY },
]

interface AppSidebarProps {
  children: ReactNode
}

export function AppSidebar({ children }: AppSidebarProps): ReactNode {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, signOutSession } = useSession()

  const visibleItems = useMemo(() => {
    const rol = user?.rol
    if (!rol) return []
    return navItems.filter((item) => item.roles.includes(rol))
  }, [user?.rol])

  const handleSignOut = async (): Promise<void> => {
    await signOutSession()
  }

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 transform border-r bg-sidebar text-sidebar-foreground transition-transform md:static md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <img
              src={logoUrl}
              alt=""
              aria-hidden
              className="h-9 w-9 rounded-full"
            />
            <span className="text-sm font-semibold">Escuela de Sommeliers</span>
          </div>
          <button
            type="button"
            className="md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Cerrar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-1 p-3">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-sidebar-accent"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                  {user ? initials(user.nombre, user.apellido) : '--'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {user ? `${user.nombre} ${user.apellido}`.trim() || user.email : ''}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {user?.email ?? ''}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(ev) => {
                  ev.preventDefault()
                  void handleSignOut()
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar sesion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      ) : null}

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="flex h-16 items-center gap-3 border-b bg-background px-4 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-semibold">Escuela de Sommeliers</span>
        </header>
        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  )
}

function initials(nombre: string, apellido: string): string {
  const a = nombre.trim().charAt(0)
  const b = apellido.trim().charAt(0)
  const result = `${a}${b}`.toUpperCase()
  return result || '--'
}
